import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";

export default function PaypalSuccess() {
  const navigate = useNavigate();
  const { refresh } = useWorkspace();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subId = params.get("subscription_id");
    if (!subId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/paypal/status/${subId}`);
        if (data.payment_status === "paid") { setStatus("paid"); await refresh(); return; }
        if (attempts.current > 10) { setStatus("failed"); return; }
      } catch {
        if (attempts.current > 10) { setStatus("failed"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="max-w-md w-full text-center rounded-lg border border-border bg-card p-10 relative z-10" data-testid="paypal-result">
        {status === "checking" && (
          <>
            <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-heading font-bold text-2xl">Confirming your PayPal subscription…</h1>
            <p className="text-muted-foreground mt-2">One moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-chart-2 mx-auto mb-6" />
            <h1 className="font-heading font-black text-2xl">You're upgraded! 🎉</h1>
            <p className="text-muted-foreground mt-2">Your PayPal subscription is active.</p>
            <Button className="mt-8 w-full" data-testid="paypal-go-dashboard" onClick={() => navigate("/dashboard")}>Go to dashboard</Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={56} weight="fill" className="text-destructive mx-auto mb-6" />
            <h1 className="font-heading font-bold text-2xl">Subscription not confirmed</h1>
            <p className="text-muted-foreground mt-2">We couldn't confirm it yet. If you completed payment, it may take a moment — check billing shortly.</p>
            <Button variant="outline" className="mt-8 w-full" data-testid="paypal-back-billing" onClick={() => navigate("/billing")}>Back to billing</Button>
          </>
        )}
      </div>
    </div>
  );
}
